import { useEffect, useState } from "react";
import { Loader2, Scale } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const LETTERS = ["A", "B", "C", "D", "E"];
const val = (l) => Number(l.line_total || 0) || Number(l.client_unit_price || 0) * Number(l.quantity || 0);
const fmt = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Greedy balance: biggest lines first, each into the part with the lowest running total.
export const autoBalance = (lines, n) => {
  const tot = Array(n).fill(0);
  const out = {};
  [...lines].sort((a, b) => val(b) - val(a)).forEach((l) => {
    const i = tot.indexOf(Math.min(...tot));
    out[l.item_id] = LETTERS[i];
    tot[i] += val(l);
  });
  return out;
};

export const SplitQuoteDialog = ({ open, onOpenChange, rfqId, cq, lines, onSaved }) => {
  const [parts, setParts] = useState(3);
  const [assign, setAssign] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    const sp = cq.split || {};
    const n = sp.parts >= 2 ? sp.parts : 3;
    setParts(n);
    setAssign(sp.parts >= 2 ? { ...autoBalance(lines, n), ...(sp.assign || {}) } : autoBalance(lines, n));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const letters = LETTERS.slice(0, parts);
  const totals = letters.map((L) => lines.filter((l) => (assign[l.item_id] || "A") === L).reduce((s, l) => s + val(l), 0));
  const save = async (n) => {
    setBusy(true);
    try {
      await api.put(`/rfqs/${rfqId}/client-quote/split`, { parts: n, assign: n >= 2 ? assign : {} });
      toast.success(n >= 2 ? `Quote split into ${n} parts` : "Split turned off");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const base = cq.quote_no || "SOM-QT";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="split-quote-dialog">
        <DialogHeader><DialogTitle>Split Quote</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-600">Parts</span>
          <div className="inline-flex rounded-lg border border-border bg-white p-1">
            {[2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => { setParts(n); setAssign(autoBalance(lines, n)); }} className={`rounded-md px-3 py-1 text-sm font-semibold ${parts === n ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`split-parts-${n}`}>{n}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setAssign(autoBalance(lines, parts))} data-testid="split-auto-balance"><Scale className="mr-1.5 h-4 w-4" />Auto-Balance</Button>
        </div>
        <div className={`grid gap-2 ${parts > 3 ? "grid-cols-2 sm:grid-cols-5" : "grid-cols-3"}`} data-testid="split-part-totals">
          {letters.map((L, i) => (
            <div key={L} className="rounded-lg border border-border px-3 py-2">
              <div className="font-mono text-[11px] font-semibold text-[#1B4383]">{base}-{L}</div>
              <div className="font-mono text-sm font-semibold text-slate-900" data-testid={`split-total-${L}`}>{cq.currency || ""} {fmt(totals[i])}</div>
              <div className="text-[11px] text-muted-foreground">{lines.filter((l) => (assign[l.item_id] || "A") === L).length} line{lines.filter((l) => (assign[l.item_id] || "A") === L).length === 1 ? "" : "s"} · excl. VAT</div>
            </div>
          ))}
        </div>
        <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <tbody>{lines.map((l) => (
              <tr key={l.item_id} className="border-b border-border last:border-0" data-testid={`split-line-${l.item_id}`}>
                <td className="px-3 py-2 text-slate-800">{l.description}</td>
                <td className="whitespace-nowrap px-2 py-2 text-right font-mono text-slate-700">{fmt(val(l))}</td>
                <td className="px-2 py-1.5">
                  <div className="flex justify-end gap-1">
                    {letters.map((L) => (
                      <button key={L} onClick={() => setAssign((a) => ({ ...a, [l.item_id]: L }))} className={`h-7 w-7 rounded-md text-xs font-bold ${(assign[l.item_id] || "A") === L ? "bg-[#1B4383] text-white" : "border border-border text-slate-500 hover:bg-slate-50"}`} data-testid={`split-assign-${l.item_id}-${L}`}>{L}</button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {(cq.split?.parts || 0) >= 2 ? <Button variant="ghost" onClick={() => save(0)} disabled={busy} data-testid="split-turn-off">Turn Off Split</Button> : <span />}
          <Button onClick={() => save(parts)} disabled={busy || totals.some((t, i) => !lines.some((l) => (assign[l.item_id] || "A") === letters[i]))} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="split-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save {parts} Parts</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
